import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/shadcn/button";
import { CalendarDays, Search, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Tournament, tournamentsQueryOptions } from "@/api/tournaments";
import { useSelectedTeam } from "@/context/teamContext";
import { useSelectedMatch } from "@/context/matchContext";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Input } from "@/components/shadcn/input";
import dayjs from "dayjs";
import { cn, dateFormat } from "@/lib/utils";

interface SelectMatchesMobileSheetProps {
    selectedMatchIds?: number[];
    onMatchSelect?: (matchIds: number[]) => void;
    useExternalState?: boolean;
}

export default function SelectMatchesMobileSheet({
    selectedMatchIds = [],
    onMatchSelect,
    useExternalState = false,
}: SelectMatchesMobileSheetProps) {
    const [open, setOpen] = useState(false);
    const [view, setView] = useState<"browse" | "selected">("browse");
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [tempSelectedMatchIds, setTempSelectedMatchIds] = useState<number[]>([]);
    const [searchQuery, setSearchQuery] = useState("");

    const contextData = useSelectedMatch();
    const { matchIds: contextMatchIds, setMatchIds, setTournamentName } = contextData || {};
    const { teamId } = useSelectedTeam();

    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));

    const currentMatchIds = useExternalState ? selectedMatchIds : contextMatchIds || [];

    const allMatches = useMemo(
        () =>
            tournaments?.flatMap((tournament) =>
                tournament.matches.map((match) => ({
                    ...match,
                    tournamentName: tournament.name,
                    tournamentId: tournament.id,
                }))
            ) || [],
        [tournaments]
    );

    const filteredMatches = useMemo(() => {
        const tournamentMatches = selectedTournament?.matches ?? [];
        const active = tournamentMatches
            .filter((match) => {
                if (!match.date) return false;
                if (match.status?.toLowerCase() === "aborted") return false;
                return true;
            })
            .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
        if (!searchQuery.trim()) return active;
        const q = searchQuery.toLowerCase();
        return active.filter((match) => {
            const date = dayjs(match.date).format("DD/MM/YYYY").toLowerCase();
            const home = match.homeTeam.shortName.toLowerCase();
            const away = match.awayTeam.shortName.toLowerCase();
            return date.includes(q) || home.includes(q) || away.includes(q);
        });
    }, [selectedTournament, searchQuery]);

    const handleTournamentSelect = (tournamentName: string) => {
        const tournament = tournaments?.find((t) => t.name === tournamentName);
        setSelectedTournament(tournament ?? null);
    };

    const toggleMatch = (matchId: number) => {
        setTempSelectedMatchIds((prev) =>
            prev.includes(matchId) ? prev.filter((id) => id !== matchId) : [...prev, matchId]
        );
    };

    const allFilteredSelected =
        filteredMatches.length > 0 && filteredMatches.every((m) => tempSelectedMatchIds.includes(m.id));
    const someFilteredSelected =
        filteredMatches.some((m) => tempSelectedMatchIds.includes(m.id)) && !allFilteredSelected;

    const toggleSelectAll = () => {
        const filteredIds = filteredMatches.map((m) => m.id);
        if (allFilteredSelected) {
            setTempSelectedMatchIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
        } else {
            setTempSelectedMatchIds((prev) => [...new Set([...prev, ...filteredIds])]);
        }
    };

    const handleConfirm = () => {
        if (useExternalState) {
            onMatchSelect?.(tempSelectedMatchIds);
        } else {
            if (setMatchIds) {
                setMatchIds(tempSelectedMatchIds);
                if (tempSelectedMatchIds.length > 0) {
                    const firstMatch = allMatches.find((m) => tempSelectedMatchIds.includes(m.id));
                    if (firstMatch && setTournamentName) {
                        setTournamentName(firstMatch.tournamentName);
                    }
                }
            }
        }
        setOpen(false);
    };

    const handleClear = () => {
        if (useExternalState) {
            onMatchSelect?.([]);
        } else {
            setMatchIds?.([]);
        }
        setOpen(false);
    };

    useEffect(() => {
        if (!open || !tournaments) return;

        setTempSelectedMatchIds([...currentMatchIds]);
        setSearchQuery("");
        setView("browse");

        if (currentMatchIds.length > 0) {
            const firstMatch = allMatches.find((m) => currentMatchIds.includes(m.id));
            if (firstMatch) {
                const tournament = tournaments.find((t) => t.matches.some((m) => m.id === firstMatch.id));
                setSelectedTournament(tournament ?? null);
            }
        } else {
            const newestTournament = [...tournaments].sort((a, b) => b.id - a.id)[0];
            setSelectedTournament(newestTournament ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) {
            setSelectedTournament(null);
        }
    }, [open]);

    return (
        <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
            <DialogPrimitive.Trigger asChild>
                <Button variant="outlined">
                    <CalendarDays size={16} className="mr-1" />
                    Select match{currentMatchIds.length > 1 ? "es" : ""}
                </Button>
            </DialogPrimitive.Trigger>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content className="fixed bottom-0 left-0 right-0 z-50 flex flex-col max-h-[88vh] bg-surface-container-lowest rounded-t-2xl shadow-[0_-8px_32px_rgba(5,0,102,0.15)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom duration-300">
                    <div className="px-5 py-4 border-b border-outline-variant/20 bg-surface-container-low rounded-t-2xl shrink-0">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-headline font-bold text-sm uppercase tracking-wider text-on-surface">
                                Select Matches
                            </h2>
                            <DialogPrimitive.Close className="p-1 rounded-md text-on-surface-variant hover:text-on-surface transition-colors">
                                <X size={18} />
                                <span className="sr-only">Close</span>
                            </DialogPrimitive.Close>
                        </div>
                        <div className="flex gap-1 bg-surface-container rounded-lg p-1">
                            <button
                                onClick={() => setView("browse")}
                                className={cn(
                                    "flex-1 py-1.5 text-xs font-headline font-bold uppercase tracking-wide rounded-md transition-colors",
                                    view === "browse"
                                        ? "bg-surface-container-lowest text-on-surface shadow-sm"
                                        : "text-on-surface-variant hover:text-on-surface"
                                )}
                            >
                                Browse
                            </button>
                            <button
                                onClick={() => setView("selected")}
                                className={cn(
                                    "flex-1 py-1.5 text-xs font-headline font-bold uppercase tracking-wide rounded-md transition-colors flex items-center justify-center gap-1.5",
                                    view === "selected"
                                        ? "bg-surface-container-lowest text-on-surface shadow-sm"
                                        : "text-on-surface-variant hover:text-on-surface"
                                )}
                            >
                                Selected
                                {tempSelectedMatchIds.length > 0 && (
                                    <span
                                        className={cn(
                                            "text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none",
                                            view === "selected"
                                                ? "bg-primary text-white"
                                                : "bg-on-surface-variant/20 text-on-surface-variant"
                                        )}
                                    >
                                        {tempSelectedMatchIds.length}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                    {view === "browse" && (
                        <div className="flex flex-col gap-3 px-5 pt-4 pb-3 shrink-0">
                            <Select value={selectedTournament?.name || ""} onValueChange={handleTournamentSelect}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select a tournament" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tournaments && tournaments.length > 0 ? (
                                        tournaments.map((t: Tournament) => (
                                            <SelectItem key={t.name} value={t.name}>
                                                {t.name}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <div className="px-3 py-2 text-on-surface-variant text-sm">
                                            No tournament data available
                                        </div>
                                    )}
                                </SelectContent>
                            </Select>

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant w-4 h-4" />
                                <Input
                                    placeholder="Search by date or team..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                />
                            </div>

                            {selectedTournament && filteredMatches.length > 0 && (
                                <label className="flex items-center gap-2 cursor-pointer py-0.5">
                                    <div className="relative">
                                        <Checkbox
                                            checked={allFilteredSelected}
                                            onCheckedChange={toggleSelectAll}
                                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                        />
                                        {someFilteredSelected && (
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <div className="w-2 h-0.5 bg-white rounded" />
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-sm text-on-surface-variant">
                                        {searchQuery
                                            ? `Select all filtered (${filteredMatches.length})`
                                            : "Select whole tournament"}
                                    </span>
                                </label>
                            )}
                        </div>
                    )}

                    {view === "browse" && (
                        <div className="overflow-y-auto flex-1 px-5">
                            {!selectedTournament ? (
                                <p className="text-center text-on-surface-variant text-sm py-10">
                                    Choose a tournament to display matches.
                                </p>
                            ) : filteredMatches.length === 0 ? (
                                <p className="text-center text-on-surface-variant text-sm py-10">No results.</p>
                            ) : (
                                <div className="flex flex-col divide-y divide-outline-variant/15">
                                    {filteredMatches.map((match) => {
                                        const isSelected = tempSelectedMatchIds.includes(match.id);
                                        return (
                                            <div
                                                key={match.id}
                                                className={cn(
                                                    "flex items-center gap-3 py-3 cursor-pointer transition-colors",
                                                    isSelected ? "bg-primary/5 -mx-5 px-5" : ""
                                                )}
                                                onClick={() => toggleMatch(match.id)}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleMatch(match.id)}
                                                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary shrink-0"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-on-surface">
                                                        {match.homeTeam.shortName} vs {match.awayTeam.shortName}
                                                    </p>
                                                    <p className="text-xs text-on-surface-variant mt-0.5">
                                                        {dayjs(match.date).format(dateFormat)}
                                                    </p>
                                                </div>
                                                <span className="text-sm font-mono text-on-surface-variant shrink-0">
                                                    {match.homeGoals} – {match.awayGoals}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {view === "selected" && (
                        <div className="overflow-y-auto flex-1 px-5 py-2">
                            {tempSelectedMatchIds.length === 0 ? (
                                <p className="text-center text-on-surface-variant text-sm py-10">
                                    No matches selected yet.
                                </p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {allMatches
                                        .filter((m) => tempSelectedMatchIds.includes(m.id))
                                        .map((match) => (
                                            <div
                                                key={match.id}
                                                className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-surface-container"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-on-surface">
                                                        {match.homeTeam.shortName} vs {match.awayTeam.shortName}
                                                    </p>
                                                    <p className="text-xs text-on-surface-variant mt-0.5">
                                                        {match.tournamentName} · {dayjs(match.date).format(dateFormat)}
                                                    </p>
                                                </div>
                                                <span className="text-sm font-mono text-on-surface-variant shrink-0 mr-1">
                                                    {match.homeGoals} – {match.awayGoals}
                                                </span>
                                                <button
                                                    onClick={() => toggleMatch(match.id)}
                                                    className="text-on-surface-variant hover:text-general-red transition-colors shrink-0"
                                                    aria-label="Remove match"
                                                >
                                                    <X size={15} />
                                                </button>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex gap-2 px-5 py-4 border-t border-outline-variant/20 bg-surface-container-low shrink-0">
                        {useExternalState && (
                            <Button
                                variant="outlined"
                                onClick={handleClear}
                                disabled={currentMatchIds.length === 0}
                                className="flex-1"
                            >
                                Clear
                            </Button>
                        )}
                        <button
                            onClick={() => setOpen(false)}
                            className="flex-1 px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-on-surface-variant rounded-md transition-colors cursor-pointer font-headline font-bold uppercase tracking-wider text-xs"
                        >
                            Cancel
                        </button>
                        <Button
                            variant="default"
                            disabled={tempSelectedMatchIds.length === 0}
                            onClick={handleConfirm}
                            className="flex-1"
                        >
                            {tempSelectedMatchIds.length > 1
                                ? `Select (${tempSelectedMatchIds.length})`
                                : "Select Match"}
                        </Button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
